import React, { useState } from 'react';
import { X, CreditCard, Coins, Wallet, ArrowRightLeft, Shuffle } from 'lucide-react';
import { formatCurrency } from '../../../shared/utils/formatUtils';
import { useModal } from '../../../shared/components/ModalProvider';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (paymentMethod: 'CARD' | 'CASH' | 'TRANSFER' | 'POINT' | 'MIXED', paymentMemo?: string) => void;
  playerName: string;
  paymentAmount: number;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  playerName,
  paymentAmount,
}) => {
  const [selectedMethod, setSelectedMethod] = useState<'CARD' | 'CASH' | 'TRANSFER' | 'POINT' | 'MIXED' | null>(null);
  const [paymentMemo, setPaymentMemo] = useState<string>('');
  const modal = useModal();

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!selectedMethod) {
      await modal.alert('결제 방법을 선택해주세요.');
      return;
    }
    if (paymentMemo.length > 200) {
      await modal.alert('메모는 200자 이하로 입력해주세요.');
      return;
    }
    onSubmit(selectedMethod, paymentMemo || undefined);
    setSelectedMethod(null);
    setPaymentMemo('');
  };

  const handleClose = () => {
    setSelectedMethod(null);
    setPaymentMemo('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 배경 오버레이 */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleClose}
      />

      {/* 모달 */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        {/* 헤더 */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-20-semibold text-gray-900">결제 처리</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* 결제 정보 */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="text-16-regular text-gray-600 mb-1">플레이어</div>
          <div className="text-18-medium text-gray-900 mb-3">{playerName}</div>
          <div className="text-16-regular text-gray-600 mb-1">결제 금액</div>
          <div className="text-24-semibold text-main">{formatCurrency(paymentAmount)}</div>
        </div>

        {/* 결제 방법 선택 */}
        <div className="mb-6">
          <div className="text-16-medium text-gray-700 mb-3">결제 방법 선택</div>
          <div className="grid grid-cols-3 gap-3">
            {/* 카드 결제 */}
            <button
              type="button"
              onClick={() => setSelectedMethod('CARD')}
              className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-colors ${
                selectedMethod === 'CARD'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <CreditCard
                size={28}
                className={selectedMethod === 'CARD' ? 'text-blue-500' : 'text-gray-400'}
              />
              <span
                className={`mt-2 text-14-medium ${
                  selectedMethod === 'CARD' ? 'text-blue-500' : 'text-gray-600'
                }`}
              >
                카드
              </span>
            </button>

            {/* 현금 결제 */}
            <button
              type="button"
              onClick={() => setSelectedMethod('CASH')}
              className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-colors ${
                selectedMethod === 'CASH'
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Wallet
                size={28}
                className={selectedMethod === 'CASH' ? 'text-purple-500' : 'text-gray-400'}
              />
              <span
                className={`mt-2 text-14-medium ${
                  selectedMethod === 'CASH' ? 'text-purple-500' : 'text-gray-600'
                }`}
              >
                현금
              </span>
            </button>

            {/* 계좌이체 */}
            <button
              type="button"
              onClick={() => setSelectedMethod('TRANSFER')}
              className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-colors ${
                selectedMethod === 'TRANSFER'
                  ? 'border-teal-500 bg-teal-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <ArrowRightLeft
                size={28}
                className={selectedMethod === 'TRANSFER' ? 'text-teal-500' : 'text-gray-400'}
              />
              <span
                className={`mt-2 text-14-medium ${
                  selectedMethod === 'TRANSFER' ? 'text-teal-500' : 'text-gray-600'
                }`}
              >
                계좌이체
              </span>
            </button>

            {/* 포인트 결제 */}
            <button
              type="button"
              onClick={() => setSelectedMethod('POINT')}
              className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-colors ${
                selectedMethod === 'POINT'
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Coins
                size={28}
                className={selectedMethod === 'POINT' ? 'text-green-500' : 'text-gray-400'}
              />
              <span
                className={`mt-2 text-14-medium ${
                  selectedMethod === 'POINT' ? 'text-green-500' : 'text-gray-600'
                }`}
              >
                라포이트 시드
              </span>
            </button>

            {/* 혼합 결제 */}
            <button
              type="button"
              onClick={() => setSelectedMethod('MIXED')}
              className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-colors ${
                selectedMethod === 'MIXED'
                  ? 'border-orange-500 bg-orange-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Shuffle
                size={28}
                className={selectedMethod === 'MIXED' ? 'text-orange-500' : 'text-gray-400'}
              />
              <span
                className={`mt-2 text-14-medium ${
                  selectedMethod === 'MIXED' ? 'text-orange-500' : 'text-gray-600'
                }`}
              >
                혼합
              </span>
            </button>
          </div>
        </div>

        {/* 결제 메모 */}
        <div className="mb-6">
          <div className="text-16-medium text-gray-700 mb-2">결제 메모 (선택)</div>
          <textarea
            value={paymentMemo}
            onChange={(e) => setPaymentMemo(e.target.value)}
            placeholder="결제 관련 메모를 입력하세요 (최대 200자)"
            maxLength={200}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main focus:border-transparent resize-none text-14-regular"
          />
          <div className="text-12-regular text-gray-400 text-right mt-1">
            {paymentMemo.length}/200
          </div>
        </div>

        {/* 버튼 */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 text-16-medium rounded-lg transition-colors"
          >
            취소
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!selectedMethod}
            className="flex-1 px-4 py-3 bg-main hover:bg-main-medium text-white text-16-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            결제 완료
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
